/**
 * What to actually do when a number goes amber.
 *
 * `numberHealth.ts` answers "how is this number", and until now that was the end of it: a chip
 * on a settings screen reading "Watch", a sentence of reason, and an operator with no idea what
 * the correct next action was. The two most common guesses are both wrong and both make things
 * worse — re-pairing a number whose sends are being refused (the connection was never the
 * problem, and a burst of re-pairs is its own signal), and doing nothing while a campaign keeps
 * feeding a number WhatsApp has started rejecting.
 *
 * So this module says, in order, what to do. It also says what the product has *already* done
 * on its own, which is not decoration: half the value of the warm-up ramp and the health-scaled
 * cap is that the operator does not have to intervene, and an operator who does not know that
 * intervenes anyway.
 *
 * Kept as data rather than as JSX for the same reason the disclosure is: the pairing screen and
 * the broadcast composer both need it, and advice that is wrong in one place because somebody
 * edited the other is worse than no advice.
 */

import type { HealthTier, NumberHealth } from './numberHealth'
import { WARMUP_DAYS, type WarmupPlan } from './warmup'

export interface PlaybookStep {
  title: string
  detail: string
}

export interface Playbook {
  tier: HealthTier
  /** One line naming what is actually happening, not what the score is. */
  headline: string
  /** Things the product has already done without being asked. */
  automatic: string[]
  /** In the order they should be done. First one first. */
  steps: PlaybookStep[]
  /** True when there is genuinely nothing to act on, so the UI can render nothing. */
  clear: boolean
}

/** Appended wherever there is no spare, because it is the fix for every case below. */
const CONNECT_A_SPARE: PlaybookStep = {
  title: 'Connect a second number',
  detail:
    'This workspace has one number that can send, so anything wrong with it stops everything. ' +
    'A spare costs the price of a SIM and lets a broadcast move across mid-send instead of ' +
    'waiting.',
}

const RESTRICTION_CHECK: PlaybookStep = {
  title: 'Look for a warning inside WhatsApp itself',
  detail:
    'Open WhatsApp on the phone that owns this number. Account restrictions appear there as a ' +
    'banner or a notice, and nothing on our side can see them. If there is one, this number is ' +
    'already flagged and no amount of pacing will unflag it — move the work to another number ' +
    'while you still can.',
}

function refusedSends(hard: boolean, hasBackup: boolean): Playbook {
  const steps: PlaybookStep[] = hard
    ? [
        {
          title: 'Do not restart or re-pair it',
          detail:
            'The connection is fine — WhatsApp is refusing the messages, which is a different ' +
            'problem. Re-pairing fixes nothing here and repeated re-pairs are themselves a ' +
            'signal worth avoiding.',
        },
        {
          title: 'Move the campaign to another number now',
          detail:
            'Broadcasts already route around this one, but anything scoped to it specifically ' +
            'will sit held. Re-scope it, or connect another number for it to move to.',
        },
        RESTRICTION_CHECK,
        {
          title: 'Use it for replies only, for about a week',
          detail:
            'Answering people who messaged you first is the traffic WhatsApp is least likely ' +
            'to object to, and it is the only thing that rebuilds a number’s standing. Replies ' +
            'are never rate limited here, so this costs you nothing.',
        },
      ]
    : [
        {
          title: 'Stop starting new conversations from this number today',
          detail:
            'Deselect it in the composer, or scope the next send to your other numbers. ' +
            'Replies to people who messaged you first are not the problem and should carry on.',
        },
        {
          title: 'Check the audience of the last send',
          detail:
            'A refused send usually means the recipient had blocked you, or was never on ' +
            'WhatsApp at all. Run the on-WhatsApp check over the list before the next campaign ' +
            'rather than after it — every message to a dead number is both a wasted credit and ' +
            'a signal against this one.',
        },
        RESTRICTION_CHECK,
        {
          title: 'Give it two or three ordinary days before the next campaign',
          detail:
            'Two-way conversation is what makes a number look like a business rather than a ' +
            'broadcaster. There is no way to hurry this part.',
        },
      ]

  return {
    tier: hard ? 'at_risk' : 'watch',
    headline: hard
      ? 'WhatsApp is refusing most of what this number sends.'
      : 'Some of what this number sends is being refused.',
    automatic: hard
      ? [
          'It has been stood down: it will not start new conversations until this clears.',
          'Broadcasts in flight are moving their remaining recipients to your other numbers.',
        ]
      : ['Today’s limit has been halved while it settles.'],
    steps: hasBackup ? steps : [...steps, CONNECT_A_SPARE],
    clear: false,
  }
}

function reconnecting(hasBackup: boolean): Playbook {
  return {
    tier: 'watch',
    headline: 'The connection to WhatsApp keeps dropping and re-opening.',
    automatic: [
      'It has been taken out of the sending pool until it settles.',
      'Recipients still queued for it are held, not failed, and go out when it is back or from ' +
        'another number.',
    ],
    steps: [
      {
        title: 'Check the phone is online and awake',
        detail:
          'A linked device follows the phone. Poor signal, aggressive battery saving, or the ' +
          'phone being off will each produce exactly this pattern, and none of them are ' +
          'anything to do with WhatsApp’s opinion of you.',
      },
      {
        title: 'Confirm Wami is still a linked device',
        detail:
          'WhatsApp → Settings → Linked devices on that phone. If our session is not listed, ' +
          'it was removed rather than dropped, and it needs pairing again with a fresh code.',
      },
      {
        title: 'Restart the connection if it has been cycling more than fifteen minutes',
        detail:
          'The Restart button below tears the socket down and rebuilds it without touching ' +
          'credentials — no new QR, no re-pairing. It is safe to use and does not count ' +
          'against the number.',
      },
      {
        title: 'If it is still cycling a day later, re-pair it once',
        detail:
          'Once, not repeatedly. A number that will not hold a link for a day after a clean ' +
          're-pair is more likely restricted than broken, and repeated pairing attempts are ' +
          'the wrong thing to be doing if so.',
      },
      ...(hasBackup ? [] : [CONNECT_A_SPARE]),
    ],
    clear: false,
  }
}

function loggedOut(hasBackup: boolean): Playbook {
  return {
    tier: 'at_risk',
    headline: 'This number is no longer linked to WhatsApp.',
    automatic: [
      'It cannot send or receive, so it has been removed from every send.',
      'Anything queued for it is held rather than failed.',
    ],
    steps: [
      {
        title: 'Find out which of the two things happened',
        detail:
          'On the phone, open WhatsApp → Settings → Linked devices. If our session is simply ' +
          'missing, somebody removed it — ordinary, and re-pairing fixes it. If WhatsApp will ' +
          'not open on that phone at all, or shows an account notice, the number itself has ' +
          'been actioned and re-pairing will not help.',
      },
      {
        title: 'Re-pair it with a fresh code',
        detail:
          'Use Reset below and scan the new QR. Your conversations here are not lost — they ' +
          'live in your workspace, not on the link.',
      },
      {
        title: 'If it logs out again within a day, stop re-pairing',
        detail:
          'A clean pairing that does not survive a day is the shape of a restriction rather ' +
          'than a glitch. Repeatedly re-pairing is the worst response to it. Move the work to ' +
          'another number and treat this one as gone.',
      },
      ...(hasBackup ? [] : [CONNECT_A_SPARE]),
    ],
    clear: false,
  }
}

function failed(hasBackup: boolean): Playbook {
  return {
    tier: 'at_risk',
    headline: 'The connection failed and could not recover on its own.',
    automatic: [
      'It has been removed from the sending pool.',
      'Queued recipients are held rather than failed.',
    ],
    steps: [
      {
        title: 'Restart the connection',
        detail:
          'The retry backoff gives up after a while; the Restart button starts it again from ' +
          'scratch. Credentials are untouched, so there is no QR to scan.',
      },
      {
        title: 'If the restart fails too, re-pair once',
        detail:
          'Reset gives a fresh QR. Do it once — see the note above about what a link that will ' +
          'not hold usually means.',
      },
      ...(hasBackup ? [] : [CONNECT_A_SPARE]),
    ],
    clear: false,
  }
}

function pairing(): Playbook {
  return {
    tier: 'watch',
    headline: 'This number is part-way through pairing.',
    automatic: ['The code refreshes on its own until it is scanned.'],
    steps: [
      {
        title: 'Scan the code from the phone that owns this number',
        detail:
          'WhatsApp → Settings → Linked devices → Link a device. It must be that phone: the ' +
          'link is what proves the number is yours.',
      },
    ],
    clear: false,
  }
}

function notConnected(reason: string, hasBackup: boolean): Playbook {
  return {
    tier: 'at_risk',
    headline: reason,
    automatic: ['It is not being given any share of a send.'],
    steps: [
      {
        title: 'Pair it',
        detail: 'Connect with a QR code below, scanned from the phone that owns this number.',
      },
      ...(hasBackup ? [] : [CONNECT_A_SPARE]),
    ],
    clear: false,
  }
}

/**
 * The one case where "healthy" still deserves a sentence.
 *
 * A brand new number sends ten messages on its first day, and a customer who has just paid for
 * a broadcast tool and watched it refuse 490 of their 500 recipients reasonably concludes it is
 * broken. Saying so before they open a ticket is the entire point.
 */
function warmingUp(warmup: WarmupPlan, hasBackup: boolean): Playbook {
  return {
    tier: 'healthy',
    headline: `This number is on day ${warmup.day} of ${WARMUP_DAYS} of its warm-up.`,
    automatic: [
      `It may start ${warmup.cap} new conversation${warmup.cap === 1 ? '' : 's'} today, and a ` +
        'little more each day.',
      'Anyone over the limit is held for the next window rather than dropped.',
    ],
    steps: [
      {
        title: 'Nothing is wrong — but do not try to hurry it',
        detail:
          'A number that pairs on Monday and messages two hundred strangers on Tuesday is the ' +
          'clearest ban pattern there is. The ramp is the single most effective thing anyone ' +
          'can do to keep a number, and it only works if it is not worked around.',
      },
      {
        title: 'Give it real conversations while it ramps',
        detail:
          'Replies are never rate limited. A number with genuine two-way traffic behind it ' +
          'finishes the ramp in far better standing than one that only ever sent its ten.',
      },
      ...(hasBackup
        ? []
        : [
            {
              title: 'Bring the spare up alongside it',
              detail:
                'Two numbers warming together reach full volume on the same day as one, and ' +
                'give a campaign somewhere to go if either is lost. Starting the second one ' +
                'later just means waiting three weeks again.',
            },
          ]),
    ],
    clear: false,
  }
}

const NOTHING_TO_DO: Playbook = {
  tier: 'healthy',
  headline: 'Connected and sending normally.',
  automatic: [],
  steps: [],
  clear: true,
}

/**
 * The playbook for one number, given everything known about it.
 *
 * Keyed off session status *and* tier rather than tier alone, because the two amber cases have
 * opposite remedies: a number that keeps reconnecting wants the socket looked at, and a number
 * whose sends are refused wants the socket left entirely alone. Collapsing them into one
 * "Watch" chip is what produced the re-pair-everything reflex this exists to replace.
 */
export function playbookFor(input: {
  health: NumberHealth
  sessionStatus: string | null
  /** Today's ramp position, when the caller has it. Only read for a healthy number. */
  warmup?: WarmupPlan | null
  /** Whether another number could carry this one's work today. */
  hasBackup: boolean
}): Playbook {
  const { health, sessionStatus, hasBackup } = input

  if (sessionStatus === 'connected') {
    if (health.tier === 'at_risk') return refusedSends(true, hasBackup)
    if (health.tier === 'watch') return refusedSends(false, hasBackup)
    if (input.warmup && !input.warmup.complete) return warmingUp(input.warmup, hasBackup)
    return NOTHING_TO_DO
  }

  if (sessionStatus === 'reconnecting') return reconnecting(hasBackup)
  if (sessionStatus === 'logged_out') return loggedOut(hasBackup)
  if (sessionStatus === 'failed') return failed(hasBackup)
  if (sessionStatus === 'awaiting_qr' || sessionStatus === 'starting') return pairing()

  return notConnected(health.reason, hasBackup)
}
